import React, { useState, useEffect } from 'react';
import { Play, Calendar, Star, Film, Clapperboard, Share2, Link, X } from 'lucide-react';
import axios from 'axios';
import { useSubscription } from '../context/SubscriptionContext';
import { tryNativeShare } from '../utils/shareHelpers';
import { matchesAnyGenre } from './GenreFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Country flags using emoji
const FLAGS = {
  US: '🇺🇸',
  UK: '🇬🇧',
  IN: '🇮🇳'
};

const COUNTRY_NAMES = {
  US: 'USA',
  UK: 'UK',
  IN: 'India'
};

const ShareMenu = ({ title, movieId, shareType = 'theatrical', onClose }) => {
  const [copied, setCopied] = useState(false);
  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}/api/share/${shareType}/${encodeURIComponent(movieId)}`;
  const shareText = `Check out "${title}" on The Movie Junkie Streaming Guide!`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
  };

  const handleTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleInstagram = async () => {
    // Instagram blocks deep-link sharing of arbitrary URLs by design. The
    // best we can do is: copy the link first, then open the Instagram app
    // (or the website if the app isn't installed) so the user just pastes
    // into a story / DM / bio. We try the `instagram://` scheme first which
    // launches the app on iOS/Android; if nothing happens within ~600ms we
    // fall back to opening instagram.com (which itself deep-links into the
    // app on mobile when installed).
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard failures (private mode / unfocused frame)
    }

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // Open the app via URI scheme. We point to the user's own home tab
      // since Instagram has no public "compose share" deep link.
      window.location.href = 'instagram://app';
      // If the app isn't installed the scheme is silently rejected — open
      // the web fallback shortly after.
      setTimeout(() => { window.open('https://www.instagram.com/', '_blank'); }, 700);
    } else {
      window.open('https://www.instagram.com/', '_blank', 'noopener');
    }
    onClose();
  };

  return (
    <div className="absolute top-12 right-0 z-20 bg-gray-900 rounded-xl shadow-2xl border border-white/10 p-2 min-w-48">
      <button
        onClick={handleCopyLink}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-lg transition-colors text-left"
      >
        <Link className="w-5 h-5 text-gray-400" />
        <span className="text-white text-sm">{copied ? 'Copied!' : 'Copy Link'}</span>
      </button>
      <button
        onClick={handleWhatsApp}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-lg transition-colors text-left"
      >
        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="text-white text-sm">WhatsApp</span>
      </button>
      <button
        onClick={handleInstagram}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-lg transition-colors text-left"
      >
        <svg className="w-5 h-5 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
        <span className="text-white text-sm">Instagram</span>
      </button>
      <button
        onClick={handleTwitter}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-lg transition-colors text-left"
      >
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
        <span className="text-white text-sm">X (Twitter)</span>
      </button>
    </div>
  );
};

const TheatricalTrailers = ({ deepLinkId, onClearDeepLink, selectedGenres = [] }) => {
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrailer, setSelectedTrailer] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const { requireSubscription } = useSubscription();

  useEffect(() => {
    fetchTrailers();
  }, []);

  // Handle deep link
  useEffect(() => {
    if (deepLinkId && trailers.length > 0) {
      const movie = trailers.find(t => t.id === deepLinkId);
      if (movie) {
        setSelectedTrailer(movie);
      }
    }
  }, [deepLinkId, trailers]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowShareMenu(false);
    if (showShareMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showShareMenu]);

  const handleCloseModal = () => {
    setSelectedTrailer(null);
    if (onClearDeepLink) {
      onClearDeepLink();
    }
  };

  const fetchTrailers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/theatrical-trailers`);
      setTrailers(response.data);
    } catch (error) {
      console.error('Error fetching theatrical trailers:', error);
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRegionStatus = (regions) => {
    if (!regions) return { released: [], playing: [] };
    
    const released = [];
    const playing = [];
    
    Object.entries(regions).forEach(([code, info]) => {
      if (info.released) {
        released.push(code);
        if (info.still_playing) {
          playing.push(code);
        }
      }
    });
    
    return { released, playing };
  };

  const filteredTrailers = trailers.filter(t => {
    // Tab (all/now_playing/upcoming) — matches original behavior.
    if (activeTab !== 'all' && t.status !== activeTab) return false;
    // Genre filter (multi-select OR) — shared with the OTT grid.
    if (!matchesAnyGenre(t, selectedGenres)) return false;
    return true;
  });

  if (loading) {
    return (
      <section className="mb-16" data-testid="theatrical-trailers-section">
        <div className="flex items-center gap-3 mb-6">
          <Clapperboard className="w-6 h-6 text-black" />
          <h3 className="font-primary text-3xl md:text-4xl uppercase tracking-wide text-black">
            In Theaters
          </h3>
        </div>
        <div className="flex items-center justify-center py-16">
          <div className="animate-pulse text-gray-600">Loading trailers...</div>
        </div>
      </section>
    );
  }

  if (trailers.length === 0) {
    return null;
  }

  return (
    <section className="mb-16" data-testid="theatrical-trailers-section">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Clapperboard className="w-6 h-6 text-black" />
          <h3 className="font-primary text-3xl md:text-4xl uppercase tracking-wide text-black">
            In Theaters
          </h3>
        </div>
        
        {/* Filter Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-full text-sm font-mono uppercase transition-all ${
              activeTab === 'all'
                ? 'bg-black text-white'
                : 'bg-white/50 text-black border border-gray-300 hover:border-black'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('now_playing')}
            className={`px-4 py-2 rounded-full text-sm font-mono uppercase transition-all ${
              activeTab === 'now_playing'
                ? 'bg-black text-white'
                : 'bg-white/50 text-black border border-gray-300 hover:border-black'
            }`}
          >
            Now Playing
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-2 rounded-full text-sm font-mono uppercase transition-all ${
              activeTab === 'upcoming'
                ? 'bg-black text-white'
                : 'bg-white/50 text-black border border-gray-300 hover:border-black'
            }`}
          >
            Coming Soon
          </button>
        </div>
      </div>

      {/* Trailers Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredTrailers.map((movie) => {
          const regionStatus = getRegionStatus(movie.regions);
          
          return (
            <div
              key={movie.id}
              className="group relative bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedTrailer(movie)}
              data-testid={`theatrical-card-${movie.id}`}
            >
              {/* Poster with Play Overlay */}
              <div className="relative aspect-video">
                {movie.backdrop_url ? (
                  <img
                    src={movie.backdrop_url}
                    alt={movie.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : movie.poster_url ? (
                  <img
                    src={movie.poster_url}
                    alt={movie.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                    <Film className="w-12 h-12 text-gray-600" />
                  </div>
                )}
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold uppercase ${
                  movie.status === 'now_playing' 
                    ? 'bg-green-500 text-black' 
                    : 'bg-yellow-500 text-black'
                }`}>
                  {movie.status === 'now_playing' ? 'In Theaters' : 'Coming Soon'}
                </div>
              </div>
              
              {/* Movie Info */}
              <div className="p-4 space-y-2">
                <h4 className="font-primary text-lg text-white uppercase tracking-wide line-clamp-1">
                  {movie.title}
                </h4>
                
                <div className="flex items-center gap-4 text-gray-400 text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span className="font-mono">{formatDate(movie.release_date)}</span>
                  </div>
                  {movie.rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500" fill="#EAB308" />
                      <span className="font-mono">{movie.rating}</span>
                    </div>
                  )}
                </div>
                
                {/* Regional Availability */}
                {regionStatus.playing.length > 0 && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-green-400 font-mono">Now Playing:</span>
                    <div className="flex gap-1">
                      {regionStatus.playing.map(code => (
                        <span key={code} title={COUNTRY_NAMES[code]} className="text-base">
                          {FLAGS[code]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {regionStatus.released.length > 0 && regionStatus.released.length !== regionStatus.playing.length && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-500 font-mono">Released in:</span>
                    <div className="flex gap-1">
                      {regionStatus.released.filter(c => !regionStatus.playing.includes(c)).map(code => (
                        <span key={code} title={`${COUNTRY_NAMES[code]} (ended)`} className="text-base opacity-50">
                          {FLAGS[code]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {movie.genres && (
                  <p className="text-gray-500 text-xs font-mono uppercase">
                    {movie.genres}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Trailer Modal - Optimized for mobile landscape */}
      {selectedTrailer && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm overflow-y-auto"
          onClick={handleCloseModal}
        >
          <div className="min-h-full flex items-start md:items-center justify-center p-2 md:p-4">
            <div
              className="relative w-full max-w-5xl bg-black rounded-xl md:rounded-2xl overflow-hidden shadow-2xl my-2 md:my-0"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with Close and Share */}
              <div className="absolute top-2 right-2 z-20 flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      requireSubscription(async () => {
                        const shareUrl = `${window.location.origin}/api/share/theatrical/${encodeURIComponent(selectedTrailer.id)}`;
                        const handled = await tryNativeShare({
                          title: selectedTrailer.title,
                          text: `Check out "${selectedTrailer.title}" on The Movie Junkie Streaming Guide!`,
                          url: shareUrl,
                        });
                        if (!handled) setShowShareMenu(prev => !prev);
                      }, 'share');
                    }}
                    data-testid="theatrical-share-btn"
                    className="p-2 bg-black/70 hover:bg-black rounded-full transition-colors"
                  >
                    <Share2 className="w-5 h-5 text-white" />
                  </button>
                  {showShareMenu && (
                    <ShareMenu title={selectedTrailer.title} movieId={selectedTrailer.id} onClose={() => setShowShareMenu(false)} />
                  )}
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 bg-black/70 hover:bg-black rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Video Container - Smaller on mobile landscape */}
              <div className="relative w-full" style={{ paddingBottom: 'min(56.25%, 50vh)' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(selectedTrailer.trailer_url)}?autoplay=1&mute=1&rel=0&playsinline=1`}
                  title={`${selectedTrailer.title} Trailer`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              
              {/* Movie Info Below Video - Scrollable on mobile */}
              <div className="p-4 md:p-6 bg-gradient-to-t from-black to-gray-900 max-h-[40vh] md:max-h-none overflow-y-auto">
                <h3 className="font-primary text-xl md:text-2xl text-white uppercase mb-2">
                  {selectedTrailer.title}
                </h3>
                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-gray-400 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                    selectedTrailer.status === 'now_playing' 
                      ? 'bg-green-500 text-black' 
                      : 'bg-yellow-500 text-black'
                  }`}>
                    {selectedTrailer.status === 'now_playing' ? 'In Theaters Now' : 'Coming Soon'}
                  </span>
                  <span className="font-mono text-sm">{formatDate(selectedTrailer.release_date)}</span>
                  {selectedTrailer.rating > 0 && (
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500" fill="#EAB308" />
                      {selectedTrailer.rating}/10
                    </span>
                  )}
                </div>
                
                {/* Regional Info in Modal */}
                {selectedTrailer.regions && (
                  <div className="flex flex-wrap gap-2 md:gap-4 mb-3 text-sm">
                    {Object.entries(selectedTrailer.regions).map(([code, info]) => (
                      <div key={code} className="flex items-center gap-1 md:gap-2">
                        <span className="text-lg">{FLAGS[code]}</span>
                        <span className="text-gray-400 text-xs md:text-sm">{COUNTRY_NAMES[code]}:</span>
                        {info.still_playing ? (
                          <span className="text-green-400 font-mono text-xs md:text-sm">Now Playing</span>
                        ) : info.released ? (
                          <span className="text-gray-500 font-mono text-xs md:text-sm">Run Ended</span>
                        ) : info.release_date ? (
                          <span className="text-yellow-400 font-mono text-xs md:text-sm">{formatDate(info.release_date)}</span>
                        ) : (
                          <span className="text-gray-600 font-mono text-xs md:text-sm">Not Scheduled</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {selectedTrailer.description && (
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {selectedTrailer.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TheatricalTrailers;
